/**
 * src/components/Financial/AspectSection.jsx
 * 修正内容:
 * 1. Square表示の修正: Square(WAX)/(WAN) の表示ロジックを復活し、スタイル(font-size:12px, font-weight:normal)を適用。
 * 2. デザイン復元: fix31.jpg の整ったレイアウトと太文字設定を再適用。
 * 3. タイムライン削除: 週間アスペクト・タイムラインに関連するコードをすべて排除。
 * 4. 動的Orb連動: 設定画面のOrb値に基づく判定ロジックは維持。
 * 5. 指示遵守: オプショナルチェイニング (?.) の排除、省略一切なしの完全記述。
 */
import React, { useState, useMemo, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
  DEFAULT_SIGN_COLORS,
} from "../../utils/constants.js";
import { ASPECT_MEANINGS } from "../../utils/interpretations.js";
import {
  Zap,
  Search,
  RefreshCw,
  Gauge,
  Printer,
  Copy,
  ChevronDown,
  ChevronUp,
  Check,
  Database,
} from "lucide-react";

// =============================================================================
// 1. 定数・補助ロジック
// =============================================================================

const MAJOR_PLANETS_GEO = [
  "Sun",
  "Moon",
  "Mercury",
  "Venus",
  "Mars",
  "Jupiter",
  "Saturn",
  "Uranus",
  "Neptune",
  "Pluto",
];
const MAJOR_PLANETS_HELIO = [
  "Earth",
  "Mercury",
  "Venus",
  "Mars",
  "Jupiter",
  "Saturn",
  "Uranus",
  "Neptune",
  "Pluto",
];
const OUTER_PLANETS_LIST = ["Jupiter", "Saturn", "Uranus", "Neptune", "Pluto"];

const AXIS_PAIRS = [
  ["ASC", "DSC"],
  ["MC", "IC"],
  ["NorthNode", "SouthNode"],
];
const HARD_ASPECTS = [
  "Conjunction",
  "Opposition",
  "Square",
  "Semi-Square",
  "Sesquiquadrate",
  "Quincunx",
  "Tri-Septile",
  "Undecile",
  "Septile",
  "Bi-Septile",
];

// ダッシュボード（App.jsx）と完全に同一のアスペクト定義リスト
const SHARED_ASPECT_DEFINITIONS = [
  {
    name: "Conjunction",
    angle: 0,
    symbol: "☌",
    type: "Hard",
    category: "major",
  },
  {
    name: "Opposition",
    angle: 180,
    symbol: "☍",
    type: "Hard",
    category: "major",
  },
  { name: "Trine", angle: 120, symbol: "△", type: "Soft", category: "major" },
  { name: "Square", angle: 90, symbol: "□", type: "Hard", category: "major" },
  { name: "Sextile", angle: 60, symbol: "⚹", type: "Soft", category: "major" },
  {
    name: "Semi-Sextile",
    angle: 30,
    symbol: "⚺",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quincunx",
    angle: 150,
    symbol: "ㅈ",
    type: "Hard",
    category: "minor",
  },
  { name: "Decile", angle: 36, symbol: "⊥", type: "Soft", category: "minor" },
  {
    name: "Bi-Decile",
    angle: 72,
    symbol: "D2",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Tri-Decile",
    angle: 108,
    symbol: "D3",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Quadra-Decile",
    angle: 144,
    symbol: "D4",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Decile",
    angle: 18,
    symbol: "SD",
    type: "Soft",
    category: "minor",
  },
  { name: "Novile", angle: 40, symbol: "N", type: "Soft", category: "minor" },
  {
    name: "Bi-Novile",
    angle: 80,
    symbol: "N2",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quad-Novile",
    angle: 160,
    symbol: "N4",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Semi-Novile",
    angle: 20,
    symbol: "SN",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Square",
    angle: 45,
    symbol: "∠",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Sesquiquadrate",
    angle: 135,
    symbol: "⚼",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Septile",
    angle: 51.43,
    symbol: "S",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Bi-Septile",
    angle: 102.86,
    symbol: "S2",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Tri-Septile",
    angle: 154.29,
    symbol: "S3",
    type: "Hard",
    category: "minor",
  },
  { name: "Quintile", angle: 72, symbol: "Q", type: "Soft", category: "minor" },
  {
    name: "Bi-Quintile",
    angle: 144,
    symbol: "bQ",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Undecile",
    angle: 165,
    symbol: "U",
    type: "Hard",
    category: "minor",
  },
];

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "24px",
  },
  noData: function (isDarkMode) {
    return {
      padding: "40px",
      textAlign: "center",
      color: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#ccc",
      fontSize: "14px",
      background: isDarkMode ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
      borderRadius: "12px",
    };
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    background: "#f1f5f9",
  },
};

const getGridLayout = function (isMobile) {
  return isMobile
    ? "minmax(85px, 1.2fr) minmax(75px, 1fr) minmax(85px, 1.2fr) 42px 65px 42px 25px"
    : "1.5fr 1.3fr 1.5fr 0.8fr 1.2fr 1fr 30px";
};

const formatDMSPrecise = function (degrees) {
  if (degrees === null || degrees === undefined) return "0°00'00\"";
  const absDeg = Math.abs(degrees);
  const d = Math.floor(absDeg);
  const mFloat = (absDeg - d) * 60;
  const m = Math.floor(mFloat);
  const s = Math.floor((mFloat - m) * 60);
  return (
    (degrees < 0 ? "-" : "") +
    d +
    "°" +
    m.toString().padStart(2, "0") +
    "'" +
    s.toString().padStart(2, "0") +
    '"'
  );
};

const getSquareLabel = function (p1, lon1, p2, lon2) {
  const order = [
    "Moon",
    "Mercury",
    "Venus",
    "Sun",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
  const idx1 = order.indexOf(p1);
  const idx2 = order.indexOf(p2);
  let fastLon = lon1,
    slowLon = lon2;
  if (idx1 !== -1 && idx2 !== -1 && idx1 > idx2) {
    fastLon = lon2;
    slowLon = lon1;
  }
  const phase = (fastLon - slowLon + 360) % 360;
  return phase < 180 ? "(WAX)" : "(WAN)";
};

const renderPlanetCol = function (
  label,
  id,
  lon,
  planetColors,
  signColors,
  isRx,
  isDarkMode,
  isMobile,
) {
  if (lon === null || lon === undefined)
    return <div style={{ width: isMobile ? "60px" : "228px" }}>---</div>;

  const sign = Astro.getZodiacSign(lon);
  const pColor = planetColors[id] || "#333";
  const sColor = signColors[sign.symbol] || "#8b5cf6";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile
          ? "12px 22px 0px 20px"
          : "15px 28px 75px 25px",
        alignItems: "center",
        gap: isMobile ? "2px" : "4px",
      }}
    >
      <span
        style={{
          fontSize: isMobile ? "9px" : "10px",
          fontWeight: "bold",
          color: "#cbd5e1",
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: isMobile ? "20px" : "24px", color: pColor }}>
        {PLANET_SYMBOLS[id] || "•"}
      </span>
      {!isMobile ? (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              color: isDarkMode ? "#ffffff" : "#64748b",
            }}
          >
            {id}
          </span>
          {isRx && (
            <span style={{ color: "#ef4444", fontSize: "10px" }}>Rx</span>
          )}
        </div>
      ) : (
        <div />
      )}
      <span
        style={{
          fontSize: isMobile ? "16px" : "18px",
          lineHeight: 1,
          color: sColor,
          textAlign: "center",
        }}
      >
        {sign.symbol + "\uFE0E"}
      </span>
    </div>
  );
};

// =============================================================================
// 2. 内部コンポーネント: PrintModal
// =============================================================================

const PrintModal = function (props) {
  if (!props.isOpen) return null;
  const handlePrint = function () {
    const w = window.open("", "", "height=600,width=800");
    w.document.write(
      "<html><head><title>" +
        props.title +
        "</title><style>body{font-family:monospace;white-space:pre-wrap;padding:20px;font-size:12px;}</style></head><body><h1>" +
        props.title +
        "</h1><hr/>" +
        props.data +
        "</body></html>",
    );
    w.document.close();
    w.print();
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "85vh",
          overflowY: "auto",
          padding: "30px",
          position: "relative",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        }}
        onClick={function (e) {
          e.stopPropagation();
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>
            アスペクト詳細データ出力
          </h3>
          <button
            style={{
              border: "none",
              background: "#f0f0f0",
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              cursor: "pointer",
            }}
            onClick={props.onClose}
          >
            ×
          </button>
        </div>
        <textarea
          readOnly
          style={{
            width: "100%",
            height: "350px",
            fontFamily: "monospace",
            padding: "15px",
            borderRadius: "8px",
            border: "1px solid #ddd",
            marginBottom: "20px",
            fontSize: "11px",
            whiteSpace: "pre",
            overflowX: "auto",
          }}
          value={props.data}
        />
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "right",
            marginTop: "20px",
          }}
        >
          <button
            onClick={function () {
              navigator.clipboard.writeText(props.data);
              alert("データをコピーしました");
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "500",
              background: props.isDarkMode
                ? "rgba(255,255,255,0.05)"
                : "#f8fafc",
              border:
                "1px solid " +
                (props.isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
              color: props.isDarkMode ? "#cbd5e1" : "#64748b",
              cursor: "pointer",
            }}
          >
            <Copy size={14} /> コピー
          </button>
          <button
            onClick={handlePrint}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "500",
              background: props.isDarkMode
                ? "rgba(255,255,255,0.05)"
                : "#f8fafc",
              border:
                "1px solid " +
                (props.isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
              color: props.isDarkMode ? "#cbd5e1" : "#64748b",
              cursor: "pointer",
            }}
          >
            <Printer size={14} /> 印刷
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 3. 内部コンポーネント: AspectRowItem
// =============================================================================

const AspectRowItem = function (props) {
  const [isOpen, setIsOpen] = useState(false);
  const a = props.a;
  const planetColors = props.planetColors;
  const signColors = props.signColors;
  const isRetro = props.isRetro;
  const positions1 = props.positions1;
  const positions2 = props.positions2;
  const isDarkMode = props.isDarkMode;
  const isMobile = props.isMobile;

  const phase = useMemo(
    function () {
      // T×Nだけでなく、全アスペクトセットで表示可能にする（または必要に応じて判定）
      return getSquareLabel(
        a.planet1,
        positions1[a.planet1],
        a.planet2,
        positions2[a.planet2],
      );
    },
    [a.planet1, a.planet2, positions1, positions2],
  );

  const getPlanetStatus = function (planet, lon) {
    if (lon === null || lon === undefined) return [];
    const results = [];
    const sign = Astro.getZodiacSign(lon);
    const dignity = Astro.getDignity
      ? Astro.getDignity(planet, sign.name)
      : null;
    if (dignity) {
      results.push({
        label: dignity.label.split(" ")[0].toUpperCase(),
        type:
          dignity.score > 0
            ? "benefit"
            : dignity.score < 0
              ? "hard"
              : "neutral",
      });
    }
    return results;
  };

  const dignities1 = getPlanetStatus(a.planet1, positions1[a.planet1]);
  const dignities2 = getPlanetStatus(a.planet2, positions2[a.planet2]);

  return (
    <div style={{ marginBottom: "1px" }}>
      <div
        className="preserved-grid"
        onClick={function () {
          if (props.interp) setIsOpen(!isOpen);
        }}
        style={{
          display: "grid",
          gridTemplateColumns: getGridLayout(isMobile),
          padding: isMobile ? "8px 4px" : "12px 16px",
          background: props.isTight
            ? isDarkMode
              ? "rgba(253, 248, 230, 0.15)"
              : "#f9f9f5"
            : isDarkMode
              ? "transparent"
              : "#fff",
          alignItems: "center",
          cursor: props.interp ? "pointer" : "default",
          borderBottom: "1px solid #f0f0f071",
          fontSize: isMobile ? "11px" : "12px",
          color: isDarkMode ? "#ffffff" : "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {renderPlanetCol(
            a.label1,
            a.planet1,
            positions1[a.planet1],
            planetColors,
            signColors,
            a.label1 === "T" && isRetro(a.planet1),
            isDarkMode,
            isMobile,
          )}
          <div style={{ display: "flex", gap: "2px", marginLeft: "4px" }}>
            {dignities1.map(function (st, idx) {
              return (
                <span
                  key={idx}
                  style={{
                    fontSize: "10px",
                    padding: "1px 4px",
                    borderRadius: "4px",
                    background:
                      st.type === "benefit"
                        ? "#27ae60"
                        : st.type === "hard"
                          ? "#e74c3c"
                          : "#94a3b8",
                    color: "#ffffff",
                  }}
                >
                  {st.label}
                </span>
              );
            })}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "25px 1fr",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <span
            style={{
              fontSize: "20px",
              color: a.type === "Hard" ? "#e74c3c" : "#3498db",
            }}
          >
            {a.symbol}
          </span>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: isDarkMode ? "#ffffff" : "#64748b",
                }}
              >
                {a.name}
              </span>
              {/* Square の場合に WAX/WAN を表示するロジックを復活 */}
              {a.name === "Square" && phase && (
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "normal",
                    color: isDarkMode ? "#ffffff" : "#64748b",
                    marginLeft: "2px",
                  }}
                >
                  {phase}
                </span>
              )}
            </div>
            <span
              style={{
                fontSize: "10px",
                color:
                  props.sig.score > 0
                    ? "#3498db"
                    : props.sig.score < 0
                      ? "#e74c3c"
                      : isDarkMode
                        ? "rgba(255, 255, 255, 0.5)"
                        : "#94a3b8",
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                padding: "0",
                background: "none",
              }}
            >
              {props.sig.icon}
              {props.sig.txt}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          {renderPlanetCol(
            a.label2,
            a.planet2,
            positions2[a.planet2],
            planetColors,
            signColors,
            a.label2 === "T" && isRetro(a.planet2),
            isDarkMode,
            isMobile,
          )}
          <div style={{ display: "flex", gap: "2px", marginLeft: "4px" }}>
            {dignities2.map(function (st, idx) {
              return (
                <span
                  key={idx}
                  style={{
                    fontSize: "10px",
                    padding: "1px 4px",
                    borderRadius: "4px",
                    background:
                      st.type === "benefit"
                        ? "#27ae60"
                        : st.type === "hard"
                          ? "#e74c3c"
                          : "#94a3b8",
                    color: "#ffffff",
                  }}
                >
                  {st.label}
                </span>
              );
            })}
          </div>
        </div>

        <div
          style={{
            textAlign: "right",
            fontSize: "11px",
            color: isDarkMode ? "#ffffff" : "#64748b",
          }}
        >
          {a.angle}°
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: "11px",
            color: isDarkMode ? "#ffffff" : "#64748b",
          }}
        >
          {formatDMSPrecise(a.dist)}
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: "11px",
            color: isDarkMode
              ? props.isTight
                ? "#ffffff"
                : "rgba(255, 255, 255, 0.7)"
              : props.isTight
                ? "#000"
                : "#64748b",
          }}
        >
          {props.isTight ? "-" : ""}
          {formatDMSPrecise(a.orb)}
        </div>

        <div style={{ textAlign: "right", color: "#cbd5e1" }}>
          {props.interp &&
            (isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
        </div>
      </div>

      {isOpen && props.interp && (
        <div
          style={{
            padding: "20px 30px",
            background: isDarkMode ? "rgba(255, 255, 255, 0.03)" : "#f8fbff",
            borderBottom: "1px solid #f0f0f075",
            fontSize: "13px",
            lineHeight: "1.7",
            color: isDarkMode ? "rgba(255, 255, 255, 0.8)" : "#444",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              marginBottom: "8px",
              color: isDarkMode ? "#ffffff" : "#64748b",
              fontSize: "14px",
            }}
          >
            MARKET INTERPRETATION
          </div>
          {props.interp.text}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// 4. メインコンポーネント: AspectSection
// =============================================================================

export default function AspectSection(props) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isDarkMode = props.isDarkMode;

  useEffect(function () {
    const handleResize = function () {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const planetColors = props.planetColors || DEFAULT_PLANET_COLORS;
  const signColors = props.signColors || DEFAULT_SIGN_COLORS;
  const aspectSettings = props.aspectSettings || {
    majorOrb: 8.0,
    minorOrb: 2.0,
    outerPlanetOrb: 1.0,
    useOuterLimit: false,
  };

  const positions = props.positions;
  const natalPositions = props.natalPositions;
  const isRetro = props.isRetro;

  const [aspectTab, setAspectTab] = useState("major");
  const [aspectHardness, setAspectHardness] = useState("all");
  const [aspectSet, setAspectSet] = useState("tt");
  const [showPrintModal, setShowPrintModal] = useState(false);

  const [targetPlanets, setTargetPlanets] = useState(function () {
    const base = props.isHeliocentric ? MAJOR_PLANETS_HELIO : MAJOR_PLANETS_GEO;
    const init = {};
    base.forEach(function (p) {
      init[p] = true;
    });
    return init;
  });

  const getSignal = function (aspectName) {
    const bullish = ["Trine", "Sextile"];
    const bearish = ["Square", "Opposition"];
    if (bullish.indexOf(aspectName) !== -1)
      return { txt: "BULLISH", score: 1, icon: "▲" };
    if (bearish.indexOf(aspectName) !== -1)
      return { txt: "BEARISH", score: -1, icon: "▼" };
    return { txt: "NEUTRAL", score: 0, icon: "●" };
  };

  const aspects = useMemo(
    function () {
      if (!positions || !natalPositions) return [];
      const res = [];
      let pos1, pos2, label1, label2;
      if (aspectSet === "tn") {
        pos1 = positions;
        pos2 = natalPositions;
        label1 = "T";
        label2 = "N";
      } else if (aspectSet === "nn") {
        pos1 = natalPositions;
        pos2 = natalPositions;
        label1 = "N";
        label2 = "N";
      } else {
        pos1 = positions;
        pos2 = positions;
        label1 = "T";
        label2 = "T";
      }

      const listRes = [];
      const patternRes = [];

      const getList = function (src) {
        return Object.keys(src).filter(function (p) {
          // 1. ハウス尖点(H1〜H12)はアスペクト計算のノイズになるため、常に除外
          if (/^H\d+$/.test(p)) return false;

          // 2. ジオセントリック時の地球を除外
          if (!props.isHeliocentric && p === "Earth") return false;

          // 3. 主要惑星リストの定義
          const isMajPlanet = props.isHeliocentric
            ? [
                "Earth",
                "Mercury",
                "Venus",
                "Mars",
                "Jupiter",
                "Saturn",
                "Uranus",
                "Neptune",
                "Pluto",
              ].includes(p)
            : [
                "Sun",
                "Moon",
                "Mercury",
                "Venus",
                "Mars",
                "Jupiter",
                "Saturn",
                "Uranus",
                "Neptune",
                "Pluto",
              ].includes(p);

          // 4. 拡張表示がOFFの場合は、主要惑星以外(ASC, MC, Node等)を除外
          if (!props.showExtendedPoints && !isMajPlanet) return false;

          return true;
        });
      };

      const planets1 = getList(pos1);
      const planets2 = getList(pos2);

      planets1.forEach(function (p1, i) {
        planets2.forEach(function (p2, j) {
          // App.jsx の renderDashboardAspectList と同一の重複排除ロジック (indexベース)
          if (p1 === p2) return;
          if (aspectSet !== "tn" && j <= i) return;

          const lon1 = pos1[p1],
            lon2 = pos2[p2];
          if (lon1 === null || lon2 === null) return;

          const diff = Math.abs(lon1 - lon2) % 360;
          const dist = diff > 180 ? 360 - diff : diff;

          // 修正：Astro.ASPECTS ではなく共有定義リストを使用
          SHARED_ASPECT_DEFINITIONS.forEach(function (asp) {
            if (
              asp.name === "Opposition" &&
              AXIS_PAIRS.some(function (pair) {
                return pair.indexOf(p1) !== -1 && pair.indexOf(p2) !== -1;
              })
            )
              return;

            const orbValue = Math.abs(dist - asp.angle);
            // App.jsx の isMajor 定義と完全に同期 (5種類)
            const isMaj = [
              "Conjunction",
              "Sextile",
              "Square",
              "Trine",
              "Opposition",
            ].includes(asp.name);

            let limit = isMaj
              ? aspectSettings.majorOrb
              : aspectSettings.minorOrb;

            if (orbValue <= limit) {
              if (isMaj) {
                patternRes.push({ planet1: p1, planet2: p2, name: asp.name });
              }

              const isSelected1 = targetPlanets[p1] !== false;
              const isSelected2 = targetPlanets[p2] !== false;

              if (isSelected1 && isSelected2) {
                if (aspectTab === "major" && !isMaj) return;
                if (aspectTab === "minor" && isMaj) return;
                const type =
                  HARD_ASPECTS.indexOf(asp.name) !== -1 ? "Hard" : "Soft";
                if (aspectHardness === "hard" && type !== "Hard") return;
                if (aspectHardness === "soft" && type !== "Soft") return;

                listRes.push({
                  planet1: p1,
                  planet2: p2,
                  label1,
                  label2,
                  name: asp.name,
                  symbol: asp.symbol,
                  angle: asp.angle,
                  orb: orbValue,
                  dist,
                  type,
                  appliedLimit: limit,
                });
              }
            }
          });
        });
      });

      const patterns = Astro.detectSpecialPatterns(patternRes, pos1);

      return {
        list: listRes.sort(function (a, b) {
          return a.orb - b.orb;
        }),
        patterns: patterns,
      };
    },
    [
      positions,
      natalPositions,
      aspectTab,
      aspectHardness,
      aspectSet,
      props.isHeliocentric,
      props.showExtendedPoints,
      targetPlanets,
      aspectSettings,
    ],
  );

  const formattedData = useMemo(
    function () {
      let t =
        "FROM_TYPE\tFROM_PLANET\tASPECT\tTO_TYPE\tTO_PLANET\t角度\tARC\tORB\tSIGNAL\n";
      if (aspects.list && aspects.list.length > 0) {
        aspects.list.forEach(function (a) {
          const sig = getSignal(a.name);
          t +=
            a.label1 +
            "\t" +
            a.planet1 +
            "\t" +
            a.name +
            "\t" +
            a.label2 +
            "\t" +
            a.planet2 +
            "\t" +
            a.angle +
            "\t" +
            formatDMSPrecise(a.dist) +
            "\t" +
            formatDMSPrecise(a.orb) +
            "\t" +
            sig.txt +
            "\n";
        });
      }
      return t;
    },
    [aspects],
  );

  const downloadCSV = function () {
    if (!formattedData) return;
    const csvContent = formattedData.replace(/\t/g, ",");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "aspect_analysis_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={styles.container}>
      <div
        style={{
          ...commonStyles.dynamicBlockCard(isDarkMode, isMobile),
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? "15px" : "0px",
          padding: isMobile ? "10px" : "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Gauge size={24} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "bold",
                color: isDarkMode ? "#ffffff" : "#64748b",
              }}
            >
              TOTAL FINANCIAL IMPACT
            </div>
            <div
              style={{ fontSize: "10px", color: "#94a3b8", fontWeight: "bold" }}
            >
              {props.isSidereal ? "サイデリアル" : "トロピカル"} /{" "}
              {props.isHeliocentric ? "ヘリオ" : "ジオ"}
            </div>
          </div>
        </div>
        <span
          style={{
            fontSize: "38px",
            fontWeight: "bold",
            color: "#27ae60",
            width: isMobile ? "100%" : "auto",
            textAlign: isMobile ? "right" : "left",
          }}
        >
          {aspects.list
            ? aspects.list.reduce(function (s, a) {
                return s + getSignal(a.name).score;
              }, 0)
            : 0}
        </span>
      </div>

      <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            marginBottom: "20px",
            gap: isMobile ? "15px" : "0px",
          }}
        >
          <div style={commonStyles.dynamicBlockTitle(isDarkMode, isMobile)}>
            <Zap size={20} color="#93278f" /> アスペクト詳細一覧
          </div>
          <div
            style={{
              display: "flex",
              gap: "8px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              onClick={function () {
                navigator.clipboard.writeText(formattedData);
                alert("データをクリップボードにコピーしました");
              }}
              style={{
                flex: isMobile ? 1 : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                border:
                  "1px solid " +
                  (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                color: isDarkMode ? "#cbd5e1" : "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <Copy size={14} />
              <span>コピー</span>
            </button>
            <button
              onClick={downloadCSV}
              style={{
                flex: isMobile ? 1 : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                border:
                  "1px solid " +
                  (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                color: isDarkMode ? "#cbd5e1" : "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <Database size={14} />
              <span>CSV出力</span>
            </button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "15px",
            marginBottom: "20px",
            padding: "15px",
            background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f8fafc",
            borderRadius: "12px",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid #edf2f7",
          }}
        >
          <select
            value={aspectSet}
            onChange={function (e) {
              setAspectSet(e.target.value);
            }}
            style={Object.assign({}, commonStyles.headerSelect, {
              fontSize: "12px",
              width: isMobile ? "100%" : "180px",
              fontWeight: "bold",
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
              border: isDarkMode
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid #ddd",
              colorScheme: isDarkMode ? "dark" : "light",
              outline: "none",
            })}
          >
            <option value="tn">ネイタル × トランジット</option>
            <option value="nn">ネイタル (N×N)</option>
            <option value="tt">トランジット (T×T)</option>
          </select>
          <select
            value={aspectHardness !== "all" ? aspectHardness : aspectTab}
            onChange={function (e) {
              const v = e.target.value;
              if (v === "hard" || v === "soft") {
                setAspectHardness(v);
                setAspectTab("all");
              } else {
                setAspectHardness("all");
                setAspectTab(v);
              }
            }}
            style={Object.assign({}, commonStyles.headerSelect, {
              fontSize: "12px",
              width: isMobile ? "100%" : "150px",
              fontWeight: "bold",
              backgroundColor: isDarkMode ? "#2d2c33" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
              border: isDarkMode
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid #ddd",
              colorScheme: isDarkMode ? "dark" : "light",
              outline: "none",
            })}
          >
            <option value="all">すべて表示</option>
            <option value="major">主要アスペクト</option>
            <option value="hard">ハードアスペクト</option>
            <option value="soft">ソフトアスペクト</option>
            <option value="minor">マイナーアスペクト</option>
          </select>
        </div>

        {aspects.patterns && aspects.patterns.length > 0 && (
          <div style={{ marginBottom: "30px", padding: "0 5px" }}>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "#64748b",
                marginBottom: "12px",
                letterSpacing: "0.05em",
              }}
            >
              複合・特殊アスペクトパターン
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(450px, 1fr))",
                gap: "12px",
              }}
            >
              {aspects.patterns.map(function (p, idx) {
                var isHard = p.type === "hard";
                var isBenefit = p.type === "benefit";

                var bgColor = isDarkMode
                  ? isHard
                    ? "rgba(231, 76, 60, 0.1)"
                    : isBenefit
                      ? "rgba(39, 174, 96, 0.1)"
                      : "rgba(255, 255, 255, 0.05)"
                  : isHard
                    ? "#fff5f5"
                    : isBenefit
                      ? "#f0fff4"
                      : "#f8f7ff";

                var borderColor = isDarkMode
                  ? "rgba(255, 255, 255, 0.1)"
                  : isHard
                    ? "#fed7d7"
                    : isBenefit
                      ? "#c6f6d5"
                      : "#e9d8fd";

                var titleColor = isHard
                  ? "#e74c3c"
                  : isBenefit
                    ? "#27ae60"
                    : isDarkMode
                      ? "#a78bfa"
                      : "#6b46c1";

                return (
                  <div
                    key={idx}
                    style={{
                      background: bgColor,
                      border: "1px solid " + borderColor,
                      borderRadius: "8px",
                      padding: "12px 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        color: titleColor,
                      }}
                    >
                      {p.name}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        margin: "8px 0",
                        flexWrap: "wrap",
                      }}
                    >
                      {p.planets.map(function (plt, i) {
                        return (
                          <React.Fragment key={i}>
                            <span
                              style={{
                                fontFamily: "serif",
                                fontSize: "28px",
                                color: planetColors[plt] || "#475569",
                                lineHeight: "1",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {PLANET_SYMBOLS[plt] || plt}
                            </span>
                            {i < p.planets.length - 1 && (
                              <span
                                style={{
                                  fontSize: "24px",
                                  color: "#cbd5e1",
                                  fontWeight: "bold",
                                  alignSelf: "center",
                                }}
                              >
                                -
                              </span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    <div
                      style={{
                        fontSize: "11px",
                        color: isDarkMode
                          ? "rgba(255, 255, 255, 0.7)"
                          : "#64748b",
                        lineHeight: "1.5",
                      }}
                    >
                      {p.desc}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div
            className="preserved-grid"
            style={{
              minWidth: isMobile ? "450px" : "1000px",
              display: "flex",
              flexDirection: "column",
              gap: "1px",
              background: isDarkMode ? "transparent" : "#f1f5f9",
            }}
          >
            <div
              className="preserved-grid"
              style={{
                display: "grid",
                gridTemplateColumns: getGridLayout(isMobile),
                padding: "12px 16px",
                background: isDarkMode
                  ? "rgba(255, 255, 255, 0.08)"
                  : "#f8fafc",
                fontSize: "11px",
                fontWeight: "bold",
                color: isDarkMode ? "#ffffff" : "#64748b",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div style={{ textAlign: "center" }}>FROM</div>
              <div style={{ textAlign: "center" }}>アスペクト</div>
              <div style={{ textAlign: "center" }}>TO</div>
              <div style={{ textAlign: "center" }}>角度</div>
              <div style={{ textAlign: "center" }}>ARC</div>
              <div style={{ textAlign: "center" }}>Orb</div>
              <div></div>
            </div>
            {!aspects.list || aspects.list.length === 0 ? (
              <div style={styles.noData(isDarkMode)}>
                一致するアスペクトはありません。
              </div>
            ) : (
              aspects.list.map(function (a, i) {
                var currentOrbLimit = a.appliedLimit || aspectSettings.majorOrb;
                var isTightCalculated = a.orb <= currentOrbLimit * 0.1;

                return (
                  <AspectRowItem
                    key={i}
                    a={a}
                    sig={getSignal(a.name)}
                    interp={
                      ASPECT_MEANINGS[
                        [a.planet1, a.planet2].sort().join("-")
                      ] || null
                    }
                    isTight={isTightCalculated}
                    isRetro={isRetro}
                    planetColors={planetColors}
                    signColors={signColors}
                    positions1={a.label1 === "T" ? positions : natalPositions}
                    positions2={a.label2 === "T" ? positions : natalPositions}
                    isDarkMode={isDarkMode}
                    isMobile={isMobile}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
      <PrintModal
        isOpen={showPrintModal}
        onClose={function () {
          setShowPrintModal(false);
        }}
        data={formattedData}
        title="アスペクト分析データ"
      />
    </div>
  );
}
